import {
  AbstractMesh,
  ArcRotateCamera,
  Engine,
  FramingBehavior,
  HardwareScalingOptimization,
  ISceneLoaderAsyncResult,
  ISceneLoaderProgressEvent,
  Mesh,
  MirrorTexture,
  MultiMaterial,
  Nullable,
  PBRMaterial,
  Plane,
  Scene,
  SceneLoader,
  SceneOptimizer,
  SceneOptimizerOptions,
  StandardMaterial,
  Vector3,
} from '@babylonjs/core';
import '@babylonjs/loaders';
import { CustomMaterial } from '@babylonjs/materials';
import React, { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Fansea3DStageInspectorV2 } from './Fansea3DStageInspectorV2';
import { Fansea3DStageMenu } from './Fansea3DStageMenu';
import { Fansea3DStageTheme } from './Fansea3DStageTheme';
import { FontFaces } from './Fonts';
import { toogleFullScreen } from './Fullscreen';
import { createScene } from './helpers/sceneHelper';

export enum Fansea3DStageWireframeMode {
  Only = 'Only',
  None = 'None',
}

export interface FanseaBackgroundProp {
  name: string;
  backgroundTextureUrl: string;
}

export interface Fansea3DStageProps {
  customBackgrounds?: FanseaBackgroundProp[] | [];
  backgroundTextureUrl: string;
  reflectionTextureUrl: string;
  blurRadius?: number;
  className?: string;
  lightScale?: number;
  hideLights?: boolean;
  hideMirror?: boolean;
  mirrorDistance?: number;
  mirrorRatio?: number;
  modelUrl: string;
  onError?: (error: Error) => void;
  onLoad?: (isLoading: boolean, progress: number) => void;
  onResize?: (event: Event) => void;
  showFPS?: boolean;
  showMenu?: boolean;
  showExport?: boolean;
  style?: React.CSSProperties;
}

const Styled3DStage = styled.div`
  ${FontFaces}
  position: relative;
  box-sizing: border-box;
  height: 100%;

  * {
    box-sizing: border-box;
  }
`;

const StyledFPS = styled.div`
  color: green;
  position: fixed;
  top: 16px;
  right: 16px;
  padding: 8px;
  font-family: Fuji, sans-serif;
`;

const Canvas = styled.canvas`
  width: 100%;
  height: 100%;
  background: 'linear-gradient(180deg, #020202, rgba(47,61,58,1) 100%)';
`;

const maxReflectionAlpha = 0.66;

export const Fansea3DStage: FC<Fansea3DStageProps> = (props) => {
  const {
    blurRadius = 10,
    className,
    lightScale = 2.0,
    hideLights,
    hideMirror,
    mirrorDistance = 0.2,
    mirrorRatio = 1.0,
    modelUrl,
    onError,
    onLoad,
    onResize,
    // reflectionTextureUrl,
    showFPS = false,
    showMenu = false,
    style,
    backgroundTextureUrl,
    showExport = false,
    customBackgrounds,
  } = props;
  const [scene, setScene] = useState<Nullable<Scene>>(null);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showInspector, setShowInspector] = useState(false);

  // Defaults
  const [theme] = useState(Fansea3DStageTheme.Dark);
  const [wireframeMode, setWireframeMode] =
    useState<Fansea3DStageWireframeMode>(Fansea3DStageWireframeMode.None);
  const [background, setBackground] = useState<String>('Dark');

  const stageRef = useRef<Nullable<HTMLDivElement>>(null);
  const optimizer = useRef<Nullable<SceneOptimizer>>(null);
  const rootMeshRef = useRef<Nullable<AbstractMesh>>(null);
  const cameraRef = useRef<Nullable<ArcRotateCamera>>(null);

  const canvasRef = useRef(null); // Get the canvas element

  useEffect(() => {
    const engine = new Engine(canvasRef.current, true); // Generate the BABYLON 3D engine
    const scene = createScene(canvasRef.current, engine, props);
    setScene(scene);

    const resizeEventListener = (event: Event) => {
      engine?.resize();
      onResize?.(event);
    };

    // Watch for browser/canvas resize events
    window.addEventListener('resize', resizeEventListener);

    return () => {
      window.removeEventListener('resize', resizeEventListener);
      scene.dispose();
    };
  }, [
    canvasRef,
    backgroundTextureUrl,
    hideLights,
    hideMirror,
    mirrorRatio,
    modelUrl,
    lightScale,
  ]);

  // set default glb-file path
  let modelRootUrl = '';
  let modelSceneFilename = '';

  // get path and glb-file name from modelURL
  if (modelUrl) {
    let pathComponents = modelUrl.split('/');
    if (pathComponents.length > 1) {
      modelSceneFilename = pathComponents.pop()!;
      modelRootUrl = `${pathComponents.join('/')}/`;
    }
  }

  const scaleTo = (
    maxDimension: number,
    mesh: AbstractMesh,
    boundingInfo: { min: Vector3; max: Vector3 }
  ) => {
    if (boundingInfo && mesh) {
      const longestDimension = Math.max(
        Math.abs(boundingInfo.min.x - boundingInfo.max.x),
        Math.abs(boundingInfo.min.y - boundingInfo.max.y),
        Math.abs(boundingInfo.min.z - boundingInfo.max.z)
      );

      const dimension = maxDimension / longestDimension;
      mesh.scaling.scaleInPlace(dimension);
    }
  };
  
  const setMaxSimultaneousLightsForAllMaterials = (scene: Scene, maxLights: number) => {
      for (const material of scene.materials) {
          if (material instanceof StandardMaterial || material instanceof PBRMaterial) {
              material.maxSimultaneousLights = maxLights;
          } else if (material instanceof MultiMaterial) {
              for (const subMaterial of material.subMaterials) {
                  if (subMaterial instanceof StandardMaterial || subMaterial instanceof PBRMaterial) {
                      subMaterial.maxSimultaneousLights = maxLights;
                  }
              }
          }
      }
  }

  const onModelLoaded = (sceneLoaderResult: ISceneLoaderAsyncResult): void => {
    const rootMesh = new AbstractMesh('model root', scene);
    rootMesh.alwaysSelectAsActiveMesh = true;

    const loadedModelMeshes: Array<AbstractMesh> = [];
    sceneLoaderResult.meshes.forEach((mesh) => {
      loadedModelMeshes.push(mesh);

      // leave meshes already parented to maintain model hierarchy:
      if (!mesh.parent) {
        mesh.parent = rootMesh;
      }
    });

    rootMeshRef.current = rootMesh;

    const divFps = document.getElementById('fps');
    const camera = scene?.activeCamera as ArcRotateCamera;
    cameraRef.current = camera;

    if (camera) {
      // Enable camera's behaviors
      var framingBehavior = camera.getBehaviorByName(
        'Framing'
      ) as FramingBehavior;
      framingBehavior.framingTime = 0;
      framingBehavior.elevationReturnTime = -1;

      if (scene) {
        scene.environmentIntensity = 0.25;
        console.log('webGL version:', scene.getEngine().webGLVersion);

        const filterMesh = (mesh: AbstractMesh): boolean =>
          mesh.isVisible &&
          mesh.isEnabled() &&
          !mesh.name.startsWith('Background') &&
          !mesh.name.startsWith('box') &&
          !mesh.name.startsWith('mirror') &&
          !mesh.name.includes('light');

        var worldExtends = scene.getWorldExtends(filterMesh);
        scaleTo(1.0, rootMesh, worldExtends);
        worldExtends = scene.getWorldExtends(filterMesh);

        const worldSize = new Vector3(
          worldExtends.max.x - worldExtends.min.x,
          worldExtends.max.y - worldExtends.min.y,
          worldExtends.max.z - worldExtends.min.z
        );

        const modelCenter: Vector3 = new Vector3(
          (worldExtends.min.x + worldExtends.max.x) / 2.0,
          (worldExtends.min.y + worldExtends.max.y) / 2.0,
          (worldExtends.min.z + worldExtends.max.z) / 2.0
        );

        framingBehavior.zoomOnBoundingInfo(worldExtends.min, worldExtends.max);

        const mirror = scene.getNodeByName('mirror') as Nullable<Mesh>;
        if (mirror) {
          const model = scene.getNodeByName('model root') as Mesh;
          const modelMeshes = model.getChildMeshes();

          // get mesh extends to get bounding box
          var meshExtends = scene.getWorldExtends(
            (mesh) => modelMeshes.includes(mesh) || mesh === model
          );

          mirror.position.y = meshExtends.min.y - worldSize.z * mirrorDistance;

          mirror.scaling = new Vector3(worldSize.x * 10, worldSize.y * 10, 1.0);
          mirror.freezeWorldMatrix();

          const reflectionMaterial: CustomMaterial = new CustomMaterial(
            'mirrorMaterial',
            scene
          ) as CustomMaterial;
          const reflectionTexture: MirrorTexture = new MirrorTexture(
            'mirror',
            { ratio: mirrorRatio },
            scene
          );
          const mirrorPlane = Plane.FromPositionAndNormal(
            mirror.position,
            new Vector3(0, -1, 0)
          ) as any;
          const reflectionMeshes = scene.meshes.filter(
            (mesh) =>
              !(
                mesh.name.toLowerCase().includes('box') ||
                mesh.name.toLowerCase().includes('background') ||
                mesh.name.toLowerCase().includes('root') ||
                mesh.name.toLowerCase().includes('mirror')
              )
          );

          mirror.material = reflectionMaterial;
          mirror.receiveShadows = false;
          reflectionTexture.renderList = reflectionMeshes;
          reflectionTexture.mirrorPlane = mirrorPlane;

          reflectionTexture.level = 0.8;
          reflectionTexture.adaptiveBlurKernel = blurRadius;
          reflectionMaterial.reflectionTexture = reflectionTexture;
          reflectionMaterial.disableLighting = true;
          reflectionMaterial.roughness = 0.8;
          reflectionMaterial.alphaMode = 7; // ALPHA_PREMULTIPLIED removes dark border around reflection
          reflectionMaterial.transparencyMode = 3; // Alpha blend and test
          reflectionMaterial.alpha = maxReflectionAlpha;
          reflectionMaterial.Fragment_Before_FragColor(`
              color.a *= reflectionColor.a;
          `); // custom fragment color declaration to support transparency where otherwise a reflection of the skybox would be visible

          mirror.setEnabled(!hideMirror);
        }

        scene.registerBeforeRender(() => {
          const activeCamera = scene.activeCamera as ArcRotateCamera;
          if (activeCamera) {
            const cameraPosition = activeCamera.position;
            const cameraY = cameraPosition.y;

            // smoothly fade mirror when moving under it
            if (mirror) {
              const mirrorAlpha = Math.sqrt(cameraY - mirror.position.y); // this funky function's sole purpose is a smooth transition

              const mirrorMaterial = mirror.material;
              if (mirrorMaterial) {
                mirrorMaterial.alpha = Math.min(
                  maxReflectionAlpha,
                  isNaN(mirrorAlpha) ? 0 : mirrorAlpha
                );
              }
            }
          }

          if (showFPS && divFps) {
            divFps.innerHTML = scene.getEngine().getFps().toFixed() + ' fps';
          }
        });

        // rendering optimisations
        scene.getAnimationRatio();
        // scene.freezeActiveMeshes();

        scene.autoClear = false; // Color buffer
        scene.autoClearDepthAndStencil = false; // Depth and stencil, obviously
        scene.blockMaterialDirtyMechanism = true;
        // mirror.doNotSyncBoundingInfo = true;
        sceneLoaderResult.meshes[0].doNotSyncBoundingInfo = true;
        // sceneLoaderResult.meshes[0].convertToUnIndexedMesh();

        const options = new SceneOptimizerOptions();
        options.addOptimization(new HardwareScalingOptimization(0, 1));

        optimizer.current = new SceneOptimizer(scene, options);

        if (camera) {
          camera.pinchPrecision = 200 / camera.radius;
          camera.upperRadiusLimit = 3 * camera.radius;

          // set a real tight Z difference between minZ and maxZ to ensure minimal Z fighting.
          camera.maxZ = (camera.upperRadiusLimit ?? 5) * 3;

          camera.setPosition(new Vector3(1.33, 1.33, 1.33));
          camera.setTarget(modelCenter);
        }
        
        setMaxSimultaneousLightsForAllMaterials(scene, 40);
      } else {
        console.warn('no root mesh');
      }
    }
  };

  const onModelProgress = (event: ISceneLoaderProgressEvent): void => {
    onLoad?.(
      event.loaded / event.total !== 1,
      event.lengthComputable ? event.loaded / event.total : 0.0
    );
  };

  useEffect(() => {
    if (scene) {
      SceneLoader.ImportMeshAsync(
        '',
        modelRootUrl,
        modelSceneFilename,
        scene,
        onModelProgress
      )
        .then(onModelLoaded)
        .catch(onError);
    }
  }, [scene]);

  return (
    <Styled3DStage ref={stageRef} className={className} style={style}>
      <Canvas ref={canvasRef} id="renderCanvas" touch-action="none"></Canvas>
      {showMenu && (
        <Fansea3DStageMenu
          onToggleFullscreen={() => {
            setIsFullscreen(!isFullscreen);

            if (stageRef.current) {
              toogleFullScreen(stageRef.current);
            }
          }}
          onToggleInspector={() => {
            setShowInspector(!showInspector);
            // console.log('show inspector: ', showInspector);
          }}
          isInspectorOpen={showInspector}
          onResetCamera={() => {
            cameraRef.current?.setPosition(new Vector3(1.33, 1.33, 1.33));
          }}
        />
      )}
      {showInspector && (
        <Fansea3DStageInspectorV2
          scene={scene}
          customBackgrounds={customBackgrounds}
          background={background}
          setBackground={(b) => {
            console.log('background: ', b);
            setBackground(b);
          }}
          wireframeMode={wireframeMode}
          setWireframeMode={setWireframeMode}
          theme={theme}
          showLights={!hideLights}
          lightScale={lightScale}
          showMirror={!hideMirror}
          mirrorDistance={mirrorDistance}
          mirrorBlur={blurRadius}
          mirrorRatio={mirrorRatio}
          showExport={showExport}
          setShowInspector={setShowInspector}
        />
      )}
      {showFPS && <StyledFPS id="fps" />}
    </Styled3DStage>
  );
};
